//-----------------------------------------------------------------------------
//
// auth.browser.js
//
// Copyright 2023 Emergence Software Pte. Ltd. - All rights reserved.
//
//-----------------------------------------------------------------------------

// Imports
import axios from "axios";


// Constants
const API_AUTH_URL         = "/api/auth/";
const API_REST_URL         = "/api/rest/1/";
const API_SUBSCRIPTION_URL = "/api/subscription/";
const API_USER_URL         = "/api/user/";


//-----------------------------------------------------------------------------
//
// FUNCTIONS
//
//-----------------------------------------------------------------------------

// Function authHeader
function authHeader() {
  const user = JSON.parse(localStorage.getItem('user'));

  if (user && user.accessToken) {
    return { 'x-access-token': user.accessToken };
  } else {
    return {};
  }
}


// Function isJson
function isJson(input_json) {
  try {
    let string_json = JSON.stringify(input_json)
    let value = JSON.parse(string_json);
    return typeof value === "object" && value !== null;
  } catch (e) {
    return false;
  }
}


// Function highestOrderErrorMessage
function highestOrderErrorMessage(err) {
  let message = err.message || 'Network error'
  if (err && err.response && err.response.data && err.response.data.message) message = err.response.data.message
  return message
}


//-----------------------------------------------------------------------------
//
// AUTHENTICATION SERVICE ROUTINES
//
//-----------------------------------------------------------------------------
class AuthServiceClass {
  // Function login
  login(email, password) {
    return axios
      .post(API_AUTH_URL + "login", {
        email,
        password
      })
      .then(response => {
        if (response.data.accessToken) {
          localStorage.setItem("user", JSON.stringify(response.data));
        }

        return response.data;
      })
      .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function logout
  logout() {
    localStorage.removeItem("user");
  }


  // Function signup
  signup(email, password) {
    return axios.post(API_AUTH_URL + "signup",
                      {
                        email,
                        password
                      }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function recovery_start
  recovery_start(email) {
    return axios.post(API_AUTH_URL + "recovery_start",
                      {
                        email
                      }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function recovery
  recovery(userId, token, email, password) {
    return axios.post(API_AUTH_URL + "recovery",
                      {
                        userId,
                        token,
                        email,
                        password
                      }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function createAccessToken
  createAccessToken(host) {
    return axios.post(API_AUTH_URL + "access_tokens",
                      {
                        host
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function deleteAccessToken
  deleteAccessToken(access_token_id) {
    return axios.delete(API_AUTH_URL + "access_token/" + access_token_id,
                        { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function getCurrentUser
  getCurrentUser() {
    return JSON.parse(localStorage.getItem('user'));
  }
}


//-----------------------------------------------------------------------------
//
// USER AUTHENTICATED SERVICE ROUTINE
//
//-----------------------------------------------------------------------------
class UserServiceClass {
  // Function getAuthorizedContent
  getAuthorizedContent(path) {
    // Critical - strip off trailing slashes as this is a security risk it will
    // trigger the router to not even check the JWT token!
    if (path)
    {
      let new_path = path
      if (path.slice(-1) === '/') {
        new_path = path.replace(/\/+$/, '') + '/'
      }
      return axios.get(new_path,
                       { headers: authHeader() }
      )
      .catch(err => { throw highestOrderErrorMessage(err) })
    }
  }


  // Function getAuthorizedRawContent
  getAuthorizedRawContent(path) {
    // Critical - strip off trailing slashes as this is a security risk it will
    // trigger the router to not even check the JWT token!
    if (path)
    {
      let new_path = path
      if (path.slice(-1) === '/') {
        new_path = path.replace(/\/+$/, '') + '/'
      }
      return axios.get(new_path,
                       {
                         headers: authHeader(),
                         responseType: 'arraybuffer'
                       }
      )
      .catch(err => { throw highestOrderErrorMessage(err) })
    }
  }


  // Function createSubscription
  createSubscription(subscription_id) {
    return axios.post(API_SUBSCRIPTION_URL + "create",
                      {
                        subscription_id
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function manageSubscription
  manageSubscription(section) {
    return axios.post(API_SUBSCRIPTION_URL + "manage",
                      {
                        section
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function applyPromotion
  applyPromotion(plan_id, promotion_id) {
    return axios.post(API_SUBSCRIPTION_URL + "promotions",
                      {
                        plan_id,
                        promotion_id
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function sendInvitation
  sendInvitation(email, message ) {
    return axios.post(API_USER_URL + "invitation",
                      {
                        email,
                        message
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function setCommunication
  setCommunication(communication ) {
    return axios.post(API_USER_URL + "communication",
                      {
                        communication
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function setOnpremise
  async setOnpremise(enabled, url, bucket) {
    // Test connection first directly from browser instance before proceeding
    // Since we just need to see a host respond - we can use fetch with no-cors for this instance
    try {
      if (enabled) {
        let response = await fetch(url,
                                   { referrerPolicy: "origin" })
        if (!response || !response.ok) throw new Error("")
      }
    }
    catch (err) {
      throw new Error("Could not connect to server")
    }

    // Update setting
    return axios.post(API_USER_URL + "on_premise",
                      {
                        on_premise: enabled,
                        on_premise_url: url,
                        on_premise_bucket: bucket
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function createDashboard
  createDashboard(name, datasetIds, data, shared=undefined) {
    if (!isJson(data)) data = undefined
    if (!isJson(datasetIds)) datasetIds = []
    return axios.post(API_REST_URL + "dashboards",
                      {
                        name,
                        data,
                        datasetIds,
                        shared
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function deleteDashboard
  deleteDashboard(dashboard_id) {
    return axios.delete(API_REST_URL + "dashboards/" + dashboard_id,
                        { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function updateDashboard
  updateDashboard(dashboard_id, data=undefined, name=undefined, datasetIds=undefined, shared=undefined, archived=undefined) {
    if (data && !isJson(data)) data = undefined
    if (datasetIds && !isJson(datasetIds)) datasetIds = undefined
    return axios.put(API_REST_URL + "dashboards/" + dashboard_id,
                      {
                        name,
                        data,
                        datasetIds,
                        shared,
                        archived
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function createDataset
  createDataset(name, data) {
    if (!isJson(data)) data = undefined
    return axios.post(API_REST_URL + "datasets",
                      {
                        name,
                        data
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function deleteDataset
  deleteDataset(dataset_id) {
    return axios.delete(API_REST_URL + "datasets/" + dataset_id,
                        { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function updateDataset
  updateDataset(dataset_id, data=undefined, name=undefined, archived=undefined) {
    if (data && !isJson(data)) data = undefined
    return axios.put(API_REST_URL + "datasets/" + dataset_id,
                      {
                        data,
                        name,
                        archived
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function createStorage
  createStorage(assetType, assetId, contentType, revisionOrAssetId, data) {
    // Sanitize input
    if (!assetType || !assetId || !contentType || !revisionOrAssetId || !data) throw new Error("")

    return axios.post(API_REST_URL + "storage/" + assetType + "/" + assetId + "/" + contentType + "/" + revisionOrAssetId,
                      data,
                      { headers: { ...authHeader(), "Content-Type": "application/octet-stream" } }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function deleteStorage
  deleteStorage(assetType, assetId, contentType, revisionOrAssetId) {
    // Sanitize input
    if (!assetType || !assetId || !contentType || !revisionOrAssetId) throw new Error("")

    return axios.delete(API_REST_URL + "storage/" + assetType + "/" + assetId + "/" + contentType + "/" + revisionOrAssetId,
                        { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }


  // Function sendShare
  sendShare(emails, message, dashboardIds) {
    return axios.post(API_REST_URL + "share",
                      {
                        emails,
                        message,
                        dashboardIds
                      },
                      { headers: authHeader() }
    )
    .catch(err => { throw highestOrderErrorMessage(err) })
  }
}


// Export functions
export const isBrowser = () => { return (typeof window !== 'undefined') ? true : false }
export const AuthService = new AuthServiceClass()
export const UserService = new UserServiceClass()
